import React from 'react'
import { Check, X, Clock, AlertCircle, Info, CheckCircle } from 'lucide-react'
import { motion } from 'framer-motion'

interface NotificationItemProps {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  read: boolean
  createdAt: string
  actionUrl?: string | null
  actionLabel?: string | null
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  type,
  title,
  message,
  read,
  createdAt,
  actionUrl,
  actionLabel,
  onMarkAsRead,
  onDelete
}) => {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-moss-500 dark:text-moss-500" />
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-ochre-500 dark:text-ochre-500" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-blood-500 dark:text-blood-500" />
      default:
        return <Info className="w-5 h-5 text-terra-500 dark:text-terra-300" />
    }
  }

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Agora'
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`
    return `${Math.floor(diffInMinutes / 1440)}d atrás`
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-3 sm:p-4 border-b border-cream-50 dark:border-ink-500 hover:bg-cream-50 dark:hover:bg-ink-700/50 transition-colors ${
        !read ? 'bg-terra-50 dark:bg-terra-500/20' : ''
      }`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <div className="flex-shrink-0 mt-1">
          {getIcon()}
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-medium break-words ${
                !read ? 'text-ink-900 dark:text-cream-50' : 'text-ink-700 dark:text-cream-300'
              }`}>
                {title}
              </h4>
              <p className="text-xs sm:text-sm text-ink-500 dark:text-ink-300 mt-1 break-words whitespace-normal">
                {message}
              </p>

              {actionUrl && actionLabel && (
                <a
                  href={actionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 mt-2 inline-block break-words"
                >
                  {actionLabel}
                </a>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {!read && (
                <button
                  onClick={() => onMarkAsRead(id)}
                  className="p-1 hover:bg-cream-200 dark:hover:bg-ink-700 rounded-caderno-xs transition-colors"
                  title="Marcar como lida"
                >
                  <Check className="w-3 h-3 text-ink-400" />
                </button>
              )}
              <button
                onClick={() => onDelete(id)}
                className="p-1 hover:bg-cream-200 dark:hover:bg-ink-700 rounded-caderno-xs transition-colors"
                title="Remover"
              >
                <X className="w-3 h-3 text-ink-400" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-1 mt-2">
            <Clock className="w-3 h-3 text-ink-400 flex-shrink-0" />
            <span className="text-xs text-ink-400 dark:text-ink-400 truncate">
              {getTimeAgo(createdAt)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default NotificationItem